import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Form, ListGroup, Badge } from 'react-bootstrap';
import { Search, Book, User, Settings, Home, ArrowRight, Command } from 'lucide-react';
import api from '../../services/api';
import './CommandPalette.css';

const CommandPalette: React.FC = () => {
  const [show, setShow] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const staticPages = [
    { id: 'home', title: 'Home', path: '/', category: 'Navigation', icon: <Home size={16} /> },
    { id: 'courses', title: 'Courses Catalog', path: '/courses', category: 'Navigation', icon: <Book size={16} /> },
    { id: 'dashboard', title: 'Student Dashboard', path: '/user', category: 'User', icon: <User size={16} /> },
    { id: 'profile', title: 'My Profile', path: '/user/profile', category: 'User', icon: <Settings size={16} /> },
  ];

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShow(prev => !prev);
      }
      if (show) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % (results.length || 1));
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex(prev => (prev - 1 + results.length) % (results.length || 1));
        } else if (e.key === 'Enter' && results[selectedIndex]) {
          e.preventDefault();
          handleNavigate(results[selectedIndex].path);
        } else if (e.key === 'Escape') {
          setShow(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [show, results, selectedIndex]);

  useEffect(() => {
    if (show) {
      setTimeout(() => inputRef.current?.focus(), 100);
      searchItems('');
    } else {
      setQuery('');
      setResults([]);
    }
  }, [show]);

  const searchItems = async (q: string) => {
    setQuery(q);
    const filteredStatic = staticPages.filter(p => 
      p.title.toLowerCase().includes(q.toLowerCase()) || 
      p.category.toLowerCase().includes(q.toLowerCase())
    );

    let courses: any[] = [];
    if (q.length > 1) {
      try {
        const res = await api.courses.list();
        const items = Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [];
        courses = items
          .filter((c: any) => c.title.toLowerCase().includes(q.toLowerCase()))
          .map((c: any) => ({
            id: `course-${c.id}`,
            title: c.title,
            path: `/courses/${c.id}`,
            category: 'Courses',
            icon: <Book size={16} />
          }));
      } catch (err) {
        console.error('Search failed', err);
      }
    }

    const allResults = [...filteredStatic, ...courses].slice(0, 8);
    setResults(allResults);
    setSelectedIndex(0);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setShow(false);
  };

  return (
    <Modal 
      show={show} 
      onHide={() => setShow(false)} 
      className="command-palette-modal"
      centered
      size="lg"
    >
      <div className="command-palette-container">
        <div className="search-input-wrapper">
          <Search className="search-icon" size={20} />
          <Form.Control
            ref={inputRef}
            type="text"
            placeholder="Search for courses, pages, settings..."
            value={query}
            onChange={(e) => searchItems(e.target.value)}
            className="search-input"
          />
          <div className="command-hint">
            <kbd>ESC</kbd> to close
          </div>
        </div>

        <div className="results-container">
          {results.length > 0 ? (
            <ListGroup variant="flush">
              {results.map((item, index) => (
                <ListGroup.Item
                  key={item.id}
                  className={`result-item ${index === selectedIndex ? 'selected' : ''}`}
                  onClick={() => handleNavigate(item.path)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="result-icon-wrapper">
                    {item.icon}
                  </div>
                  <div className="result-content">
                    <span className="result-title">{item.title}</span>
                    <Badge bg="secondary" className="result-category">{item.category}</Badge>
                  </div>
                  <ArrowRight className="enter-icon" size={14} />
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <div className="no-results">
              {query ? `No results for "${query}"` : 'Start typing to search...'}
            </div>
          )}
        </div>

        <div className="command-footer">
          <div className="footer-item">
            <kbd>↑↓</kbd> <span>to navigate</span>
          </div>
          <div className="footer-item">
            <kbd>↵</kbd> <span>to select</span>
          </div>
          <div className="footer-item ms-auto">
            <Command size={14} className="me-1" /> <span>DataPilot Command</span>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CommandPalette;
